"use client";

import { c as _c } from "react/compiler-runtime";
import { PlusCircle } from "lucide-react";
import { useDemoStore } from "@/stores/demo-store";
import { prepareEditorNavigation } from "@/lib/editor/navigation-guard";
import { cn } from "@/lib/utils";

/**
 * Chapter list for the `/try` demo — the CHAPTERS header, the ordered chapter
 * buttons, and the "Add Chapter" action. Rendered both inline (the desktop left
 * rail, ≥lg) and inside the mobile "Chapters" sheet, so the two can't drift.
 *
 * `onNavigate` fires after the user selects or adds a chapter — the mobile
 * sheet passes a close handler so picking a chapter dismisses the drawer.
 * `showCount` is suppressed inside the mobile sheet, where the header's
 * top-right corner is taken by the Sheet's built-in close button.
 */
export function DemoChapterList(t0) {
  const $ = _c(30);
  const {
    onNavigate,
    showCount: t1
  } = t0;
  const showCount = t1 === undefined ? true : t1;
  const chapters = useDemoStore(_temp);
  const activeChapterId = useDemoStore(_temp2);
  const setActiveChapter = useDemoStore(_temp3);
  const addChapter = useDemoStore(_temp4);
  let t2;
  if ($[0] !== activeChapterId || $[1] !== chapters) {
    t2 = chapters.find(c => c.id === activeChapterId) ?? chapters[0];
    $[0] = activeChapterId;
    $[1] = chapters;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const active = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Chapters</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== chapters.length || $[5] !== showCount) {
    t4 = showCount && <span className="text-xs text-muted-foreground">{chapters.length}</span>;
    $[4] = chapters.length;
    $[5] = showCount;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <div className="flex items-center justify-between px-4 py-3">{t3}{t4}</div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== active || $[10] !== chapters || $[11] !== onNavigate || $[12] !== setActiveChapter) {
    let t7;
    if ($[14] !== active || $[15] !== onNavigate || $[16] !== setActiveChapter) {
      t7 = c_0 => <button key={c_0.id} onClick={async () => {
        if (c_0.id === active.id || !(await prepareEditorNavigation())) {
          return;
        }
        setActiveChapter(c_0.id);
        onNavigate?.();
      }} className={cn("mb-1 flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition", c_0.id === active.id ? "bg-primary/10 font-medium text-primary" : "text-foreground hover:bg-muted")}><span className="truncate">{c_0.sortOrder}: {c_0.title}</span>{c_0.status === "draft" && <span className="ml-2 shrink-0 text-[10px] uppercase text-muted-foreground">draft</span>}</button>;
      $[14] = active;
      $[15] = onNavigate;
      $[16] = setActiveChapter;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t6 = chapters.slice().sort(_temp5).map(t7);
    $[9] = active;
    $[10] = chapters;
    $[11] = onNavigate;
    $[12] = setActiveChapter;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <nav className="flex-1 overflow-y-auto px-2">{t6}</nav>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== addChapter || $[21] !== onNavigate) {
    t8 = async () => {
      if (!(await prepareEditorNavigation())) {
        return;
      }
      addChapter();
      onNavigate?.();
    };
    $[20] = addChapter;
    $[21] = onNavigate;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <PlusCircle className="h-4 w-4" />;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8) {
    t10 = <div className="border-t border-border p-2"><button onClick={t8} className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm font-medium text-primary transition hover:bg-primary/20">{t9}Add Chapter</button></div>;
    $[24] = t8;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t5 || $[28] !== t7) {
    t11 = <div className="flex h-full flex-col">{t5}{t7}{t10}</div>;
    $[26] = t10;
    $[27] = t5;
    $[28] = t7;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function _temp5(a, b) {
  return a.sortOrder - b.sortOrder;
}
function _temp4(s_2) {
  return s_2.addChapter;
}
function _temp3(s_1) {
  return s_1.setActiveChapter;
}
function _temp2(s_0) {
  return s_0.activeChapterId;
}
function _temp(s) {
  return s.chapters;
}